import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, HelpCircle, X } from "lucide-react";
import "./ActionDialog.css";

const ActionDialogContext = createContext({
  confirmAction: async () => false,
  requestText: async () => null,
});

export const ActionDialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [value, setValue] = useState("");
  const [validation, setValidation] = useState("");
  const resolver = useRef(null);
  const inputRef = useRef(null);

  const finish = useCallback(result => {
    const resolve = resolver.current;
    resolver.current = null;
    setDialog(null);
    setValue("");
    setValidation("");
    resolve?.(result);
  }, []);

  const open = useCallback(options => new Promise(resolve => {
    resolver.current?.(null);
    resolver.current = resolve;
    setValue(options.defaultValue || "");
    setValidation("");
    setDialog(options);
  }), []);

  const confirmAction = useCallback(options => open({ type: "confirm", ...options }), [open]);
  const requestText = useCallback(options => open({ type: "prompt", required: true, ...options }), [open]);

  useEffect(() => {
    if (!dialog) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = event => {
      if (event.key === "Escape") finish(dialog.type === "confirm" ? false : null);
    };
    window.addEventListener("keydown", onKeyDown);
    if (dialog.type === "prompt") window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [dialog, finish]);

  const submit = event => {
    event.preventDefault();
    if (dialog.type === "prompt") {
      const answer = value.trim();
      if (dialog.required && !answer) {
        setValidation(dialog.validationMessage || "Please enter a reason to continue.");
        inputRef.current?.focus();
        return;
      }
      finish(answer || null);
      return;
    }
    finish(true);
  };

  const contextValue = useMemo(() => ({ confirmAction, requestText }), [confirmAction, requestText]);

  return <ActionDialogContext.Provider value={contextValue}>
    {children}
    {dialog && <div className="action-dialog-overlay" role="presentation">
      <section className="action-dialog" role="dialog" aria-modal="true" aria-labelledby="action-dialog-title" aria-describedby="action-dialog-message">
        <button type="button" className="action-dialog-close" aria-label="Close dialog" onClick={() => finish(dialog.type === "confirm" ? false : null)}>
          <X size={20} aria-hidden="true" />
        </button>
        <div className={`action-dialog-icon${dialog.danger ? " danger" : ""}`} aria-hidden="true">
          {dialog.danger ? <AlertTriangle size={24} /> : <HelpCircle size={24} />}
        </div>
        <div className="action-dialog-copy">
          <h2 id="action-dialog-title">{dialog.title || "Please confirm"}</h2>
          <p id="action-dialog-message">{dialog.message || "Are you sure you want to continue?"}</p>
        </div>
        <form onSubmit={submit}>
          {dialog.type === "prompt" && <label className="action-dialog-field">
            <span>{dialog.inputLabel || "Reason"}{dialog.required && <b aria-hidden="true"> *</b>}</span>
            <textarea
              ref={inputRef}
              rows="3"
              maxLength={dialog.maxLength || 300}
              value={value}
              onChange={event => { setValue(event.target.value); if (validation) setValidation(""); }}
              placeholder={dialog.placeholder || "Enter a short reason"}
              aria-invalid={Boolean(validation)}
              aria-describedby={validation ? "action-dialog-error" : undefined}
            />
            <small>{value.length}/{dialog.maxLength || 300}</small>
            {validation && <em id="action-dialog-error" role="alert">{validation}</em>}
          </label>}
          <div className="action-dialog-actions">
            <button type="button" className="action-dialog-secondary" onClick={() => finish(dialog.type === "confirm" ? false : null)}>
              {dialog.cancelLabel || "Cancel"}
            </button>
            <button type="submit" className={`action-dialog-primary${dialog.danger ? " danger" : ""}`}>
              {dialog.confirmLabel || "Confirm"}
            </button>
          </div>
        </form>
      </section>
    </div>}
  </ActionDialogContext.Provider>;
};

export const useActionDialog = () => useContext(ActionDialogContext);
